import { useEffect, useState, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { useAppSelector, useAppDispatch } from "../../../app/hooks";
import { SimpleOption } from "../../../components/input/SimpleListbox";
import {
	setDistrictFilter as setGlobalDistrictFilter,
	setSchoolFilter as setGlobalSchoolFilter,
	setTeacherFilter as setGlobalTeacherFilter,
	setCourseFilter as setGlobalCourseFilter,
	setActiveSubTab as setGlobalActiveSubTab,
} from "../../../app/adminReportsFilterSlice";
import { getParamAsInt } from "../../../app/utils";
import { UserRole } from "../../../app/userSlice";
import { getMaxReportLevel } from "./adminReportsUtil";
import {
	useGetDistrictOptionsForReportsQuery,
	ReportLevel,
	useLazyGetSchoolOptionsForReportsQuery,
	useLazyGetTeacherOptionsForSchoolForReportsQuery,
	useLazyGetCourseOptionsForTeacherForReportsQuery,
	ProctorCourseViewModel,
} from "./adminReportsApiSlice";
import { AdminReportsSubTabs } from "./AdminReports";

// This hook is made to allow the filters to interact and remain constant along the entire reports stream
// across all admin report pages, so that a change made on one, changes the filters on another page

export function useAdminReportsFilters() {
	const dispatch = useAppDispatch();
	const { role, school, district } = useAppSelector((state) => state.user);
	const districtFilter = useAppSelector((state) => state.adminReportsFilter.districtFilter);
	const schoolFilter = useAppSelector((state) => state.adminReportsFilter.schoolFilter);
	const teacherFilter = useAppSelector((state) => state.adminReportsFilter.teacherFilter);
	const courseFilter = useAppSelector((state) => state.adminReportsFilter.courseFilter);
	const activeSubTab = useAppSelector((state) => state.adminReportsFilter.activeSubTab);

	const [queryParams, setSearchParams] = useSearchParams();
	const districtIdParam = getParamAsInt(queryParams, "districtId") ?? null;
	const schoolIdParam = getParamAsInt(queryParams, "schoolId") ?? null;

	const maxReportLevel = getMaxReportLevel(role);

	// Store both sets of options - one for Admin level, one for Course level
	const [adminLevelDistrictOptions, setAdminLevelDistrictOptions] = useState<SimpleOption[]>([]);
	const [adminLevelSchoolOptions, setAdminLevelSchoolOptions] = useState<SimpleOption[]>([]);
	const [courseLevelDistrictOptions, setCourseLevelDistrictOptions] = useState<SimpleOption[]>(
		[]
	);
	const [courseLevelSchoolOptions, setCourseLevelSchoolOptions] = useState<SimpleOption[]>([]);

	const [districtOptions, setDistrictOptions] = useState<SimpleOption[]>([]);
	const [schoolOptions, setSchoolOptions] = useState<SimpleOption[]>([]);
	const [teacherOptions, setTeacherOptions] = useState<SimpleOption[]>([]);
	const [courseOptions, setCourseOptions] = useState<ProctorCourseViewModel[]>([]);

	// Track previous tab to detect tab changes
	const previousTabRef = useRef<string | null>(null);

	// Get both sets of district options
	const getAdminDistrictsResult = useGetDistrictOptionsForReportsQuery({ level: maxReportLevel });
	const adminLevelDistricts = getAdminDistrictsResult.data;

	const getCourseDistrictsResult = useGetDistrictOptionsForReportsQuery({
		level: ReportLevel.Course,
	});
	const courseLevelDistricts = getCourseDistrictsResult.data;

	const [callGetSchoolsByDistrict] = useLazyGetSchoolOptionsForReportsQuery();
	const [callGetTeachersBySchool] = useLazyGetTeacherOptionsForSchoolForReportsQuery();
	const [callGetCoursesByTeacher] = useLazyGetCourseOptionsForTeacherForReportsQuery();

	useEffect(() => {
		// Return the appropriate options based on active tab
		setDistrictOptions(
			activeSubTab === AdminReportsSubTabs.Course
				? courseLevelDistrictOptions
				: adminLevelDistrictOptions
		);
		setSchoolOptions(
			activeSubTab === AdminReportsSubTabs.Course
				? courseLevelSchoolOptions
				: adminLevelSchoolOptions
		);
	}, [
		activeSubTab,
		courseLevelSchoolOptions,
		adminLevelSchoolOptions,
		courseLevelDistrictOptions,
		adminLevelDistrictOptions,
	]);

	const setDistrictFilter = (value: number) => {
		dispatch(setGlobalDistrictFilter(value));
	};

	const setSchoolFilter = (value: number) => {
		dispatch(setGlobalSchoolFilter(value));
	};

	const setTeacherFilter = (value: number) => {
		dispatch(setGlobalTeacherFilter(value));
	};

	const setCourseFilter = (value: number) => {
		dispatch(setGlobalCourseFilter(value));
	};

	const setActiveSubTab = (value: string) => {
		dispatch(setGlobalActiveSubTab(value));
	};

	// Reset filters when switching tabs if selected district/school is not available in new tab
	useEffect(() => {
		const previousTab = previousTabRef.current;

		// Only run when tab actually changes
		if (previousTab !== null && previousTab !== activeSubTab) {
			// Check if current district is still valid in new tab
			if (districtFilter && districtFilter !== 0) {
				const newDistrictOptions =
					activeSubTab === AdminReportsSubTabs.Admin
						? adminLevelDistrictOptions
						: courseLevelDistrictOptions;

				const isDistrictValid = newDistrictOptions.some(
					(opt) => Number(opt.value) === Number(districtFilter)
				);
				if (!isDistrictValid) {
					if (maxReportLevel === ReportLevel.State) {
						setDistrictFilter(0);
					} else {
						setDistrictFilter(Number(newDistrictOptions[0]?.value) ?? 0);
					}

					previousTabRef.current = activeSubTab;
					return; // District reset will cascade to school/teacher/course
				}
			}

			// Check if current school is still valid in new tab
			if (schoolFilter && schoolFilter !== 0) {
				const newSchoolOptions =
					activeSubTab === AdminReportsSubTabs.Admin
						? adminLevelSchoolOptions
						: courseLevelSchoolOptions;

				const isSchoolValid = newSchoolOptions.some(
					(opt) => Number(opt.value) === Number(schoolFilter)
				);
				if (!isSchoolValid && maxReportLevel !== ReportLevel.School) {
					setSchoolFilter(0);
					previousTabRef.current = activeSubTab;
					return; // School reset will cascade to teacher/course
				}
			}

			// Update the previous tab reference after validation
			previousTabRef.current = activeSubTab;
		} else if (previousTab === null && activeSubTab !== null) {
			// Initial tab set - just update the ref without validation
			previousTabRef.current = activeSubTab;
		}
	}, [
		activeSubTab,
		adminLevelDistrictOptions,
		adminLevelSchoolOptions,
		courseLevelDistrictOptions,
		courseLevelSchoolOptions,
		districtFilter,
		schoolFilter,
		maxReportLevel,
	]);

	// Clear query params after they've been applied to avoid them overwriting the manual changes
	useEffect(() => {
		if (districtIdParam !== null || schoolIdParam !== null) {
			setSearchParams({}, { replace: true });
		}
	}, [districtIdParam, schoolIdParam]);

	// Get Admin-level district options
	useEffect(() => {
		if (adminLevelDistricts) {
			const options = adminLevelDistricts.map(
				(d) => ({ value: d.value, label: d.text }) as SimpleOption
			);

			if (maxReportLevel === ReportLevel.State) {
				options.unshift({ value: 0, label: "All Districts" } as SimpleOption);
			}

			setAdminLevelDistrictOptions(options);

			// Only set district filter if we're on the Admin tab
			if (activeSubTab === AdminReportsSubTabs.Admin) {
				// Set from URL param if present, otherwise set default if not already set
				if (districtIdParam !== null) {
					setDistrictFilter(districtIdParam);
				} else if (!districtFilter) {
					if (
						maxReportLevel == ReportLevel.School ||
						maxReportLevel == ReportLevel.District
					) {
						const selectedDistrict = Number(
							adminLevelDistricts.find((d) => d.selected === true)?.value ?? 0
						);
						setDistrictFilter(selectedDistrict);
					} else {
						setDistrictFilter(0);
					}
				}
			}
		}
	}, [adminLevelDistricts, districtIdParam, activeSubTab]);

	// Get Course-level district options
	useEffect(() => {
		if (courseLevelDistricts) {
			const options = courseLevelDistricts.map(
				(d) => ({ value: d.value, label: d.text }) as SimpleOption
			);
			if (maxReportLevel === ReportLevel.State) {
				options.unshift({ value: 0, label: "All Districts" } as SimpleOption);
			}

			setCourseLevelDistrictOptions(options);

			// Only set district filter if we're on the Course tab
			if (activeSubTab === AdminReportsSubTabs.Course) {
				// Set from URL param if present, otherwise set default if not already set
				if (districtIdParam !== null) {
					setDistrictFilter(districtIdParam);
				} else if (!districtFilter) {
					if (
						maxReportLevel == ReportLevel.School ||
						maxReportLevel == ReportLevel.District
					) {
						const selectedDistrict = Number(
							courseLevelDistricts.find((d) => d.selected === true)?.value ?? 0
						);
						setDistrictFilter(selectedDistrict);
					} else {
						setDistrictFilter(0);
					}
				}
			}
		}
	}, [courseLevelDistricts, districtIdParam, activeSubTab]);

	// Get Admin-level school options
	useEffect(() => {
		// skip call before ready for max level of School
		// skip if school admin school not in a district
		if (
			(maxReportLevel === ReportLevel.School && !districtFilter) ||
			(role === UserRole.SchoolAdmin && school && !district)
		)
			return;

		callGetSchoolsByDistrict(
			{
				level: maxReportLevel,
				districtId: districtFilter === 0 ? undefined : districtFilter,
			},
			true
		)
			.unwrap()
			.then((schools) => {
				if (schools) {
					const options = schools.map(
						(s) => ({ value: s.value, label: s.text }) as SimpleOption
					);

					if (maxReportLevel !== ReportLevel.School) {
						options.unshift({ value: 0, label: "All Schools" } as SimpleOption);
					}

					setAdminLevelSchoolOptions(options);

					// Only set school filter if we're on the Admin tab
					if (activeSubTab === AdminReportsSubTabs.Admin) {
						// Set from URL param if present, otherwise set default if not already set
						if (schoolIdParam !== null) {
							setSchoolFilter(schoolIdParam);
						} else if (!schoolFilter) {
							if (maxReportLevel == ReportLevel.School) {
								const selectedSchool = Number(
									schools.find((s) => s.selected === true)?.value ?? 0
								);
								setSchoolFilter(selectedSchool);
							} else {
								setSchoolFilter(0);
							}
						}
					}
				}
			});
	}, [districtFilter, schoolIdParam, activeSubTab]);

	// Get Course-level school options
	useEffect(() => {
		// skip call before ready for max level of School
		// let school admins in a school with no district pass by bypassing the check for !districtFilter via checking district
		if (
			maxReportLevel === ReportLevel.School &&
			!districtFilter &&
			role === UserRole.SchoolAdmin &&
			school &&
			district
		)
			return;

		callGetSchoolsByDistrict(
			{
				level: ReportLevel.Course,
				districtId: districtFilter === 0 ? undefined : districtFilter,
			},
			true
		)
			.unwrap()
			.then((schools) => {
				if (schools) {
					const options = schools.map(
						(s) => ({ value: s.value, label: s.text }) as SimpleOption
					);

					if (maxReportLevel !== ReportLevel.School) {
						options.unshift({ value: 0, label: "All Schools" } as SimpleOption);
					}

					setCourseLevelSchoolOptions(options);

					// Only set school filter if we're on the Course tab
					if (activeSubTab === AdminReportsSubTabs.Course) {
						// Set from URL param if present, otherwise set default if not already set
						if (schoolIdParam !== null) {
							setSchoolFilter(schoolIdParam);
						} else if (!schoolFilter) {
							if (maxReportLevel == ReportLevel.School) {
								const selectedSchool = Number(
									schools.find((s) => s.selected === true)?.value ?? 0
								);
								setSchoolFilter(selectedSchool);
							} else {
								setSchoolFilter(0);
							}
						}
					}
				}
			});
	}, [districtFilter, schoolIdParam, activeSubTab]);

	useEffect(() => {
		if (!schoolFilter || schoolFilter === 0) {
			setTeacherOptions([]);
			return;
		}

		callGetTeachersBySchool(schoolFilter)
			.unwrap()
			.then((teachers) => {
				if (teachers) {
					const options = teachers.map(
						(t) => ({ value: t.id, label: t.name }) as SimpleOption
					);

					if (!teacherFilter && teachers.length > 0) {
						setTeacherFilter(teachers[0].id);
					}

					setTeacherOptions(options);
				}
			});
	}, [schoolFilter]);

	useEffect(() => {
		if (!teacherFilter || teacherFilter === 0 || !schoolFilter) {
			setCourseOptions([]);
			return;
		}

		callGetCoursesByTeacher({ proctorId: teacherFilter, schoolId: schoolFilter })
			.unwrap()
			.then((courses) => {
				if (courses) {
					if (!courseFilter && courses.length > 0) {
						setCourseFilter(courses[0].courseId);
					}

					setCourseOptions(courses);
				}
			});
	}, [teacherFilter]);

	return {
		maxReportLevel,
		districtOptions,
		schoolOptions,
		teacherOptions,
		courseOptions,
		districtFilter,
		schoolFilter,
		teacherFilter,
		courseFilter,
		activeSubTab,
		setDistrictFilter,
		setSchoolFilter,
		setTeacherFilter,
		setCourseFilter,
		setActiveSubTab,
	};
}
